
import React from "react";
import { i18nManager } from "../../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, ITournamentEdition, ITournamentEditionRegisteredParticipant, ITournamentEditionPrize, ITournamentEditionPrizePoolActivities, SanitizedResponse, TournamentPrizeType, TournamentService } from "../../../../../services";
import Snapper from "../../../../shared/Snapper";
import { AlertInterface, BoolHelper, Button, ButtonManageRef, Classname, Column, FormControl, Scheme, StringHelper, Table, TextInput } from "@rivtn/noseur";
import { Schemes, Utils } from "../../../../../utils";
import { Elements } from "../../../../shared/Elements";
import { ConfirmDialog, Siren } from "../../../../shared/ConfirmDialog";
import PrizePoolDetail from "./shared/PrizePoolDetail";
import { AddOrEditTournamentPrize } from "../../create_tournament/sections/PrizePool";

interface ResultAndPrizeDistributionProps {
    tournamentEdition: ITournamentEdition;
    dataComponentState: [React.ReactElement | undefined, React.Dispatch<React.SetStateAction<React.ReactElement | undefined>>];
}

function ResultAndPrizeDistribution(props: ResultAndPrizeDistributionProps) {

    const tournamentService = TournamentService.getInstance();
    const [dataComponent, setDataComponent] = props.dataComponentState;
    const labels = i18nManager.Labels.host.manage_tournament.result_prize_distro;
    const [tournamentEdition, setTournamentEdition] = React.useState(props.tournamentEdition);
    const [tournamentPrizes, setTournamentPrizes] = React.useState<ITournamentEditionPrize[]>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [tournamentPrizePoolActivities, setTournamentPrizePoolActivities] = React.useState<ApiResponseData<ITournamentEditionPrizePoolActivities> | undefined>({});

    React.useEffect(() => {
        fetchInformation();

        return () => {
            setDataComponent(undefined);
        };
    }, []);

    return (<div className="mt-participants">
        <div className="mt-participants-navbar" style={{ alignItems: "center" }}>
            <div>
                <Button raised scheme={Schemes.OMNEHOST} text={labels.announce_result} disabled={tournamentEdition.meta.result_announced} onClick={announceResult} />
                <Button raised scheme={Schemes.OMNEHOST} text={labels.distribute_prize_pool} disabled={!tournamentEdition.meta.result_announced || tournamentEdition.meta.prize_pool_distributed} onClick={distributePrizePool} />
            </div>
            {(!dataComponent) && (<div>
                <ReactRouterDom.NavLink to={`/hosts/${tournamentEdition.tournament.host.public_id}/tournament/${tournamentEdition.tournament?.external_id}/result-prize-distro/historic`}>{labels.view_historical_prize_pool}</ReactRouterDom.NavLink>
                <span className="link" onClick={() => AddOrEditTournamentPrize({
                    type: "TROPHY",
                    tournamentEdition,
                    fetchInformations: fetchInformation,
                    prizeCountries: Object.keys(tournamentEdition.registration ?? {}).map((currencyCode) => {
                        return i18nManager.getCurrencyCountryCode(currencyCode);
                    }),
                })}>{labels.add_new_prize}</span>
                <Snapper header={labels.prize_pool_media_snap} template={[{ p: "tour_name", v: tournamentEdition.name }]} />
            </div>)}
        </div>
        <div className="mt-prize-view">
            <div className="mt-prize-view-prizes">
                <div className="mt-prize-view-prize">
                    <div className="header">
                        <Snapper style={{ marginRight: 10 }} header={labels.top_winners_media_snap} template={[{ p: "tour_name", v: tournamentEdition.name }]} />
                        <span style={{ fontWeight: "bold" }}>{labels.top_winner}</span>
                    </div>
                    <div className="prize-pool-prizes" style={{ overflow: "auto", alignItems: "baseline" }}>
                        {(tournamentPrizes) ? (tournamentPrizes.filter((p) => p.type === "TOP_WINNER").map((prize) => (<div key={prize.external_id} className={Classname.build({ "active": (dataComponent as any)?.key === prize?.external_id })}
                            onClick={() => {
                                Utils.updateSearchParams("sprize", prize.external_id, setSearchParams);
                                setDataComponent(PrizePoolDetail({ tournamentEdition, tournamentPrize: prize, dataComponentState: props.dataComponentState, refetch: () => fetchInformation(true), setSearchParams }));
                            }}>
                            <span className="text-shadowed">{Utils.numberWithNth(prize.position)}</span>
                            <img alt={prize.title} src={prize.logo_url} />
                            {(!!(prize.manually_selected_winner ?? prize.projected_winner)?.external_id) && (<div className="projected-winner">
                                <img alt={(prize.manually_selected_winner ?? prize.projected_winner)?.external_id} src={Utils.getResourceImage((prize.manually_selected_winner ?? prize.projected_winner)?.resource)} />
                                <span>{Utils.getResourceName((prize.manually_selected_winner ?? prize.projected_winner)?.resource)}</span>
                            </div>)}
                            {(BoolHelper.equalsAny(prize.prize_type, [TournamentPrizeType.MONETARY, TournamentPrizeType.MONETARY_AND_PHYSICAL_PRIZE])) && (<div className="prizes">
                                {Object.keys(tournamentEdition.registration ?? {}).map((currency) => {
                                    const countryPrizePool = prize?.currency_prizes?.[currency];
                                    if (!countryPrizePool) return null;
                                    return (<span key={currency} style={{ marginTop: 5 }}>{StringHelper.toPresentableMoneyValue(countryPrizePool)} {currency}</span>);
                                })}
                            </div>)}
                        </div>))) : (<section style={{ flex: 1, height: 50, marginTop: 50 }}>{Elements.LOADING_STATE2}</section>)}
                    </div>
                </div>
                <div className="mt-prize-view-prize" style={{ marginTop: 50 }}>
                    <div className="header">
                        <Snapper style={{ marginRight: 10 }} header={labels.trophies_prize_media_snap} template={[{ p: "tour_name", v: tournamentEdition.name }]} />
                        <span style={{ fontWeight: "bold" }}>{labels.trophies}</span>
                    </div>
                    <div className="prize-pool-prizes" style={{ overflow: "auto", alignItems: "baseline" }}>
                        {(tournamentPrizes) ? (tournamentPrizes.filter((p) => p.type === "TROPHY").map((prize) => (<div key={prize.external_id} className={Classname.build({ "active": (dataComponent as any)?.key === prize?.external_id })}
                            onClick={() => {
                                Utils.updateSearchParams("sprize", prize.external_id, setSearchParams);
                                setDataComponent(PrizePoolDetail({ tournamentEdition, tournamentPrize: prize, dataComponentState: props.dataComponentState, refetch: () => fetchInformation(true), setSearchParams }));
                            }}>
                            <img alt={prize.title} src={prize.logo_url} />
                            <p className="lined-limit" style={{ marginTop: 10, marginBottom: 0 }}>{prize.title}</p>
                            {(!!(prize.manually_selected_winner ?? prize.projected_winner)?.external_id) && (<div className="projected-winner">
                                <img alt={(prize.manually_selected_winner ?? prize.projected_winner)?.external_id} src={Utils.getResourceImage((prize.manually_selected_winner ?? prize.projected_winner)?.resource)} />
                                <span>{Utils.getResourceName((prize.manually_selected_winner ?? prize.projected_winner)?.resource)}</span>
                            </div>)}
                            {(BoolHelper.equalsAny(prize.prize_type, [TournamentPrizeType.MONETARY, TournamentPrizeType.MONETARY_AND_PHYSICAL_PRIZE])) && (<div className="prizes">
                                {Object.keys(tournamentEdition.registration ?? {}).map((currency) => {
                                    const countryPrizePool = prize?.currency_prizes?.[currency];
                                    if (!countryPrizePool) return null;
                                    return (<span key={currency} style={{ marginTop: 5 }}>{StringHelper.toPresentableMoneyValue(countryPrizePool)} {currency}</span>);
                                })}
                            </div>)}
                        </div>))) : (<section style={{ flex: 1, height: 50, marginTop: 50 }}>{Elements.LOADING_STATE2}</section>)}
                    </div>
                </div>
            </div>
            {(!dataComponent) && (<div className="mt-prize-view-pool">
                <div>
                    <p style={{ fontWeight: "bold", fontSize: 16 }}>{i18nManager.Labels.host.create_tournament.prize_pool.pools}</p>
                    {Object.keys(tournamentEdition.registration ?? {}).map((currencyCode) => {
                        return i18nManager.getCurrencyCountryCode(currencyCode);
                    }).map((prizeCountry) => {
                        const countryName = i18nManager.getCountryName(prizeCountry);
                        const currency = i18nManager.getCountryCurrency(prizeCountry);
                        const isTournamentCountry = prizeCountry === tournamentEdition.country_code;
                        const countryPrizePool = tournamentEdition.meta?.price_pool_amounts?.[currency];
                        const totalTopWinnerPhysicalPrizes = tournamentPrizes?.
                            filter((p) => BoolHelper.equalsAny(p.prize_type, [TournamentPrizeType.MONETARY_AND_PHYSICAL_PRIZE, TournamentPrizeType.PHYSICAL_PRIZE]))?.
                            filter((p) => p.physical_prize?.available_in_all_countries || isTournamentCountry);

                        return (<span key={prizeCountry} style={{ marginBottom: 15 }}>
                            {countryName}{": "}
                            {!countryPrizePool && !totalTopWinnerPhysicalPrizes?.length && (<b>{i18nManager.Labels.host.create_tournament.prize_pool.no_prize}</b>)}
                            {!!countryPrizePool && (<b>{StringHelper.toPresentableMoneyValue(countryPrizePool)} {currency}</b>)}{" "}
                            {!!countryPrizePool && !!totalTopWinnerPhysicalPrizes?.length && (<b>, </b>)}
                            {!!totalTopWinnerPhysicalPrizes?.length && (<b>{totalTopWinnerPhysicalPrizes?.length} {i18nManager.Labels.host.create_tournament.prize_pool.physical_prize}</b>)}
                        </span>);
                    })}
                </div>
                <div style={{ marginTop: 40, flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontWeight: "bold", fontSize: 16 }}>{labels.activities}</span>
                        <span className="link" onClick={exportActivities}>{labels.export}</span>
                    </div>
                    <Table stripedRows scheme={Schemes.OMNEHOST} data={tournamentPrizePoolActivities?.content} totalRecords={tournamentPrizePoolActivities?.total_elements} dataRefreshKeys={["external_id", "created_at", "log"]}
                        hideHeaders style={{ flex: 1, width: "100%", marginTop: 20 }} rowsPerPage={parseInt(searchParams.get("size") ?? "100")} loadingState={Elements.LOADING_STATE}>
                        <Column className="table-c" dataKey="log" template={(v) => {
                            return (<span className="inline-markdowner" dangerouslySetInnerHTML={{ __html: "• " + Utils.markdownToHtml(v) }} />);
                        }} />
                    </Table>
                    {Elements.buildPaginator(tournamentPrizePoolActivities ?? {},
                        ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchInformation),
                        parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
                </div>
            </div>)}
        </div>
    </div>);

    function fetchInformation(ppOnly: boolean = false) {
        const now = new Date();
        if (ppOnly) setDataComponent(undefined);
        tournamentService.getTournamentEditionPrizePool(tournamentEdition.external_id!).then(({ content }: SanitizedResponse<ITournamentEditionPrize>) => {
            setTournamentPrizes(content.map((c) => {
                c.logo_url = c.logo_url + "?" + now.getTime();
                return c;
            }));
            for (const prize of (content ?? [])) {
                if (prize.external_id === searchParams.get("sprize")) {
                    setDataComponent?.(PrizePoolDetail({ tournamentEdition, tournamentPrize: prize, dataComponentState: props.dataComponentState, refetch: () => fetchInformation(true), setSearchParams }));
                    break;
                }
            }
        }).catch(BaseService.reportError);
        if (!ppOnly) {
            tournamentService.queryPrizePoolActivities(tournamentEdition.external_id!, {
                sort: "id,DESC",
                page: parseInt(searchParams.get("page") ?? "1"),
                size: parseInt(searchParams.get("size") ?? "100"),
            }).then(({ apiResponse }: SanitizedResponse<ITournamentEditionPrizePoolActivities>) => {
                setTournamentPrizePoolActivities(apiResponse.data!);
            }).catch(BaseService.reportError);
        }
    }

    function announceResult() {
        let tournamentName = "";
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.announce_result;

        const dialog = ConfirmDialog({
            closable: true,
            desc: dialogLabels.desc,
            style: { maxWidth: 900 },
            title: dialogLabels.title,
            confirmLabel: dialogLabels.announce,
            content: (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
                <a href={`/hosts/${tournamentEdition.tournament.host.public_id}/tournament/${tournamentEdition.tournament?.external_id}/scoring`} style={{ marginTop: 5 }}>{dialogLabels.view_leaderboard}</a>
                <a href={dialogLabels.what_happens.location} target="_blank" style={{ marginTop: 5 }}>{dialogLabels.what_happens.label}</a>
                <span style={{ marginTop: 15 }}>{i18nManager.Labels.tournaments.dialog.confirm_tournament.to_continue}</span>
                <FormControl isFieldset fill label={i18nManager.Labels.tournaments.dialog.confirm_tournament.tour_name} style={{ marginTop: 15 }}>
                    <TextInput scheme={Schemes.OMNEHOST} style={{ width: "100%" }} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            rightContent: (<div style={{ borderLeft: "1px solid var(--underlineColor)", marginTop: 10, height: "100%" }}>
                <div style={{ borderBottom: "1px solid var(--underlineColor)", borderTop: "1px solid var(--underlineColor)", padding: 10, fontWeight: "bold" }}>
                    {dialogLabels.tournament_winners}
                </div>
                <div style={{ display: "flex", flexDirection: "column", paddingTop: 10 }}>
                    {tournamentPrizes?.filter((p) => p.type === "TOP_WINNER" && !!p.projected_winner)?.map((tournamentPrize) => {
                        const winner = tournamentPrize.projected_winner;
                        const name = Utils.getResourceName(winner?.resource);
                        const image = Utils.getResourceImage(winner?.resource);

                        return (<div key={tournamentPrize.external_id} style={{ display: "flex", alignItems: "center", margin: 15, marginTop: 0 }}>
                            <span className="text-shadowed" style={{ fontSize: 16, fontWeight: "bold", color: "var(--invertedTextColor)", "--textShadowColor": "var(--textColor)" } as any}>{Utils.numberWithNth(tournamentPrize.position)}</span>
                            <img alt={name} src={image} style={{ width: 25, height: 25, marginLeft: 10, marginRight: 5 }} />
                            <span>{name}</span>
                        </div>);
                    })}
                </div>
                <div style={{ borderBottom: "1px solid var(--underlineColor)", borderTop: "1px solid var(--underlineColor)", padding: 10, fontWeight: "bold", marginTop: 10 }}>
                    {dialogLabels.trophies}
                </div>
                <div style={{ display: "flex", flexDirection: "column", paddingTop: 10 }}>
                    {tournamentPrizes?.filter((p) => p.type === "TROPHY" && !!p.projected_winner)?.map((tournamentPrize) => {
                        const winner = tournamentPrize.projected_winner;
                        const name = Utils.getResourceName(winner?.resource);
                        const image = Utils.getResourceImage(winner?.resource);

                        return (<React.Fragment key={tournamentPrize.external_id}>
                            <span style={{ margin: 10, fontWeight: 500 }}>{tournamentPrize.title}</span>
                            <div style={{ display: "flex", alignItems: "center", margin: 15, marginTop: 0 }}>
                                <img alt={name} src={image} style={{ width: 25, height: 25, marginLeft: 10, marginRight: 5 }} />
                                <span>{name}</span>
                            </div>
                        </React.Fragment>);
                    })}
                </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentName !== tournamentEdition.tournament.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.tournaments.dialog.confirm_tournament.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                tournamentService.announceResults(tournamentEdition.external_id).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchInformation(true);
                        Siren.alert(dialogLabels.announcement_successful);
                        setTournamentEdition({ ...tournamentEdition, meta: { ...tournamentEdition.meta, result_announced: true } })
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function distributePrizePool() {
        let tournamentName = "";
        const dialogLabels = labels.dialog.distribute;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            desc: dialogLabels.desc,
            style: { maxWidth: 900 },
            title: dialogLabels.title,
            confirmLabel: dialogLabels.distribute,
            content: (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
                <a href={dialogLabels.authorizing.location} target="_blank">{dialogLabels.authorizing.label}</a>
                <span style={{ marginTop: 15 }}>{i18nManager.Labels.tournaments.dialog.confirm_tournament.to_continue}</span>
                <FormControl isFieldset fill label={i18nManager.Labels.tournaments.dialog.confirm_tournament.tour_name} style={{ marginTop: 15 }}>
                    <TextInput scheme={Schemes.OMNEHOST} style={{ width: "100%" }} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            rightContent: (<div style={{ borderLeft: "1px solid var(--underlineColor)", marginTop: 10, height: "100%" }}>
                <div style={{ borderBottom: "1px solid var(--underlineColor)", borderTop: "1px solid var(--underlineColor)", padding: 10, fontWeight: "bold" }}>
                    {labels.dialog.announce_result.tournament_winners}
                </div>
                <div style={{ display: "flex", flexDirection: "column", paddingTop: 10 }}>
                    {tournamentPrizes?.filter((p) => p.type === "TOP_WINNER" && !!(p.projected_winner ?? p.manually_selected_winner))?.map((tournamentPrize) => {
                        let winnerPrizeAmount, currency;
                        const winner = tournamentPrize.projected_winner ?? tournamentPrize.manually_selected_winner;
                        const name = Utils.getResourceName(winner?.resource);
                        const image = Utils.getResourceImage(winner?.resource);

                        if (BoolHelper.equalsAny(tournamentPrize.prize_type, [TournamentPrizeType.MONETARY, TournamentPrizeType.MONETARY_AND_PHYSICAL_PRIZE])) {
                            currency = i18nManager.getCountryCurrency(winner?.resource?.country_code ?? "NGA");
                            if (!(currency in (tournamentPrize.currency_prizes ?? {}))) currency = Object.keys(tournamentPrize.currency_prizes ?? {})?.[0];
                            winnerPrizeAmount = tournamentPrize.currency_prizes?.[currency];
                        }

                        return (<div key={tournamentPrize.external_id} style={{ display: "flex", alignItems: "center", margin: 15, marginTop: 0 }}>
                            <span className="text-shadowed" style={{ fontSize: 16, fontWeight: "bold", color: "var(--invertedTextColor)", "--textShadowColor": "var(--textColor)" } as any}>{Utils.numberWithNth(1)}</span>
                            <img alt={name} src={image} style={{ width: 25, height: 25, marginLeft: 10, marginRight: 5 }} />
                            <span>{name} -</span>
                            <img alt={tournamentPrize.external_id} src={tournamentPrize.logo_url} style={{ width: 20, height: 20, marginLeft: 10, marginRight: 10 }} />
                            {(!!tournamentPrize.physical_prize?.name) && (<b style={{ marginRight: 5 }}>{tournamentPrize.physical_prize.name},</b>)}
                            {(!!winnerPrizeAmount && currency) && (<b>{i18nManager.getCurrencySign(currency)} {StringHelper.toPresentableMoneyValue(winnerPrizeAmount)}</b>)}
                        </div>);
                    })}
                </div>
                <div style={{ borderBottom: "1px solid var(--underlineColor)", borderTop: "1px solid var(--underlineColor)", padding: 10, fontWeight: "bold", marginTop: 10 }}>
                    {labels.dialog.announce_result.trophies}
                </div>
                <div style={{ display: "flex", flexDirection: "column", paddingTop: 10 }}>
                    {tournamentPrizes?.filter((p) => p.type === "TROPHY" && !!(p.projected_winner ?? p.manually_selected_winner))?.map((tournamentPrize) => {
                        let winnerPrizeAmount, currency;
                        const winner = tournamentPrize.projected_winner ?? tournamentPrize.manually_selected_winner;
                        const name = Utils.getResourceName(winner?.resource);
                        const image = Utils.getResourceImage(winner?.resource);

                        if (BoolHelper.equalsAny(tournamentPrize.prize_type, [TournamentPrizeType.MONETARY, TournamentPrizeType.MONETARY_AND_PHYSICAL_PRIZE])) {
                            currency = i18nManager.getCountryCurrency(winner?.resource?.country_code ?? "NGA");
                            if (!(currency in (tournamentPrize.currency_prizes ?? {}))) currency = Object.keys(tournamentPrize.currency_prizes ?? {})?.[0];
                            winnerPrizeAmount = tournamentPrize.currency_prizes?.[currency];
                        }

                        return (<React.Fragment key={tournamentPrize.external_id}>
                            <span style={{ margin: 10, fontWeight: 500 }}>{tournamentPrize.title}</span>
                            <div style={{ display: "flex", alignItems: "center", margin: 15, marginTop: 0 }}>
                                <img alt={name} src={image} style={{ width: 25, height: 25, marginLeft: 10, marginRight: 5 }} />
                                <span>{name} -</span>
                                <img alt={tournamentPrize.external_id} src={tournamentPrize.logo_url} style={{ width: 20, height: 20, marginLeft: 10, marginRight: 10 }} />
                                {(!!tournamentPrize.physical_prize?.name) && (<b style={{ marginRight: 5 }}>{tournamentPrize.physical_prize.name},</b>)}
                                {(!!winnerPrizeAmount && currency) && (<b>{i18nManager.getCurrencySign(currency)} {StringHelper.toPresentableMoneyValue(winnerPrizeAmount)}</b>)}
                            </div>
                        </React.Fragment>);
                    })}
                </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentName !== tournamentEdition.tournament.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.tournaments.dialog.confirm_tournament.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                tournamentService.distributePrizes(tournamentEdition.external_id).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchInformation(true);
                        Siren.alert(dialogLabels.distribution_successful);
                        setTournamentEdition({ ...tournamentEdition, meta: { ...tournamentEdition.meta, prize_pool_distributed: true } })
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function exportActivities() {
        const rows = [[labels.activities]]
            .concat((tournamentPrizePoolActivities?.content ?? []).map((v) => [
                Utils.formatToFineDate(v.created_at, true),
                v.log ?? "",
            ]));
        Utils.downloadBlob(rows.map(row => row.map(String)
            .map((v: any) => v.replaceAll('"', '""'))
            .map((v: any) => `"${v}"`)
            .join(",")
        ).join('\r\n'), `omnehost_tournament_${tournamentEdition.name}_prize_pool_activities_${searchParams.get("from") ?? "2025-02-01 01:00:00 GMT"}_${searchParams.get("to") ?? Utils.formatToApiDate(new Date())}`);
    }

}

export default ResultAndPrizeDistribution;
